import { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material"
import { useForm } from "react-hook-form";
import { BackdropComponent, Controls } from "../../../components";
import DialogModalHOC from "../components/DialogModalHOC";
import { theme } from "../../../utils/theme/godzilla_light";
import TransMessage from "../transMessage";
import { Trans, useTranslation } from "react-i18next";
import { IInfoComponentProps } from "../../../types/editProfile.types";
import { personalFormSchema, personalFormDefaults } from "./personalnfo.helper";
import { useMutation } from "react-query";
import { updateOnboardingStatusURL, instance as axiosInstance, withCredentials } from "../../../services";
import { useAppContext, useSnackbar } from "../../../contexts";
import { formatPhoneNumber } from "../../../utils/regex.utils";
import { validateErrorMsg } from "../../../utils/error.utils";
import { supportMailUrl } from "../../../constants";

const EditPersonalInfo = (props: IInfoComponentProps) => {
    const { isVisible, onCloseDialog, data } = props
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down(950))

    const { providerId } = useAppContext();
    const { showSnackbar } = useSnackbar();
    const [isInfoUpdateLoading, setIsInfoUpdateLoading] = useState(false)

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset: formReset,
        watch,
        getValues,
        setValue
    } = useForm<any>({
        resolver: personalFormSchema(t),
        defaultValues: personalFormDefaults
    });

    const emailFieldValue = getValues("email_address")
    const phoneNumber = watch("phone_number");

    useEffect(() => {
        if (data) {
            const editPersonalFormValues = { ...personalFormDefaults };
            editPersonalFormValues.first_name = data?.firstName;
            editPersonalFormValues.last_name = data?.lastName;
            editPersonalFormValues.email_address = data?.emails?.find((email: any) => email?.isPrimary)?.email || ""
            editPersonalFormValues.phone_number = data?.phones?.find((phone: any) => phone?.isPrimary)?.number || ""
            formReset(editPersonalFormValues)
        }
    }, [data, formReset])

    useEffect(() => {
        if (phoneNumber) {
            const formattedNumber = formatPhoneNumber(phoneNumber);
            if (formattedNumber !== phoneNumber) {
                setValue("phone_number", formattedNumber);
            }
        }
    }, [phoneNumber, setValue, formReset]);

    const savePersonalInfoMutation = useMutation((params: any) => {
        setIsInfoUpdateLoading(true)
        return axiosInstance().put(updateOnboardingStatusURL(providerId), params, withCredentials())
    },
        {
            onSuccess: () => {
                if(onCloseDialog){
                    onCloseDialog()
                }
            },
            onError: (error: any) => {
                showSnackbar(validateErrorMsg(error?.data?.details[0]), "error")
            },
            onSettled: () => {
                setIsInfoUpdateLoading(false)
            }
        }
    );

    const onSave = (formInfoValues: any) => {
        const personalInfoParams = {
            "providerId": providerId,
            "firstName": formInfoValues.first_name,
            "lastName": formInfoValues.last_name,
            "phones": [
                {
                    "countryCode": "+1",
                    "number": formInfoValues.phone_number ?? "",
                    "extension": "101",
                    "type": "WORK",
                    "isPrimary": true
                }
            ]
        }
        savePersonalInfoMutation.mutate(personalInfoParams)
    }

    return (
        <DialogModalHOC
            boxTitle={t(TransMessage.PERSONAL_TITLE)}
            isOpen={isVisible}
            handleSave={handleSubmit(onSave)}
            onClose={onCloseDialog}>
            <Box component={'form'}
                sx={{
                    mt: "1.5rem",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                }}>
                <Controls.Input
                    name="first_name"
                    label={t(TransMessage.FIRST_NAME_LABEL)}
                    defaultValue={''}
                    control={control}
                    required
                    error={errors.first_name || ''}
                />
                <Controls.Input
                    name="last_name"
                    label={t(TransMessage.LAST_NAME_LABEL)}
                    defaultValue={''}
                    control={control}
                    required
                    error={errors.last_name || ''}
                />
                <Controls.Input
                    name="email_address"
                    label={t(TransMessage.EMAIL_LABEL)}
                    defaultValue={emailFieldValue}
                    infoIcon
                    infoContent={t(TransMessage.TOOLTIP_EMAIL)}
                    control={control}
                    disabled
                />
                <Controls.Input
                    name="phone_number"
                    label={t(TransMessage.PHONE_LABEL)}
                    defaultValue={''}
                    control={control}
                    error={errors.phone_number || ''}
                />

                <Typography
                    sx={{ ...theme.typography.poppinsBodyRegular, mt: "12px", fontSize: "12px", textAlign: isMobile ? "center" : "left" }}>
                    <Trans
                        i18nKey={t(TransMessage.DELETE_LABEL)}
                        components={{
                            br: <br />,
                            // eslint-disable-next-line
                            a: <a href={`mailto:${supportMailUrl}`} style={{ ...theme.typography.poppinsBodyRegular, fontSize: "12px", textUnderlineOffset: "4px", textDecorationColor: theme.palette.primary.main, textDecorationThickness: "2px" }}></a>
                        }} />
                </Typography>
                <BackdropComponent showBackdrop={isInfoUpdateLoading} />
            </Box>
        </DialogModalHOC>
    )
}

export default EditPersonalInfo